import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session.service';
import { url } from '../config/app.constants';
import { hasVerifiedEmail } from './session.guard-util';

/**
 * Requires a signed-in user.
 *
 * Every guard in the app it replaces was written as
 *
 *     someObservable.subscribe(v => { if (bad) this.router.navigate([...]); });
 *     return true;
 *
 * which allows the navigation immediately and only corrects course later. The guarded
 * component mounted and began fetching before the check finished, and each navigation
 * leaked a subscription that was never closed. These guards return their decision.
 *
 * A verified email is part of being signed in, as the garage app gates it on every
 * launch. The console checked it only on the login form, so a session restored from a
 * previous visit — or an account signed in before it was verified — walked straight in,
 * and met a rules refusal on every read: every email-keyed rule wants a verified email.
 */
export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const session = inject(SessionService);
  const router = inject(Router);

  await auth.ready();

  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    // Carrying the target lets login return the user where they were headed instead of
    // dropping them on the default page.
    return router.createUrlTree([url.login.path], { queryParams: { returnUrl: state.url } });
  }
  if (!(await hasVerifiedEmail(auth))) {
    return router.createUrlTree([url.verifyEmail.path]);
  }

  session.initialize();
  await session.whenReady();

  return true;
};
